import {
  MAX_FILE_SIZE,
  SUPPORTED_MIME_TYPES,
} from '../constants/fileValidation';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
  maxRetries?: number;
  retryDelay?: number;
  fileId?: string;
  uploaderId?: string;
}

/**
 * Upload a file to S3 using a pre-signed URL with retry mechanism
 */
export const uploadFileToS3 = async (
  file: File,
  presignedUrl: string,
  options: UploadOptions = {},
): Promise<void> => {
  const {
    onProgress,
    signal,
    maxRetries = 3,
    retryDelay = 1000,
    fileId,
    uploaderId,
  } = options;

  let lastError: Error = new Error('Unknown error');

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      await uploadFileToS3Single(file, presignedUrl, {
        onProgress,
        signal,
        fileId,
        uploaderId,
      });
      return;
    } catch (error) {
      lastError = error as Error;

      if (lastError.message.includes('aborted')) {
        throw lastError;
      }

      if (attempt === maxRetries) {
        break;
      }

      const delay = retryDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw new Error(
    `Upload failed after ${maxRetries + 1} attempts: ${lastError.message}`,
  );
};

/**
 * Single upload attempt to S3 using a pre-signed URL
 */
const uploadFileToS3Single = async (
  file: File,
  presignedUrl: string,
  options: Pick<
    UploadOptions,
    'onProgress' | 'signal' | 'fileId' | 'uploaderId'
  > = {},
): Promise<void> => {
  const { onProgress, signal, fileId, uploaderId } = options;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress: UploadProgress = {
            loaded: event.loaded,
            total: event.total,
            percentage: Math.round((event.loaded / event.total) * 100),
          };
          onProgress(progress);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'));
    });

    if (signal) {
      signal.addEventListener('abort', () => {
        xhr.abort();
      });
    }

    xhr.open('PUT', presignedUrl);

    if (fileId) {
      xhr.setRequestHeader('x-amz-meta-fileid', fileId);
    }
    if (uploaderId) {
      xhr.setRequestHeader('x-amz-meta-uploaderid', uploaderId);
    }

    xhr.send(file);
  });
};

/**
 * Get file type based on MIME type
 * @deprecated Use mimeTypeToLegacyType from fileAdapters instead
 */
export const getFileType = (file: File): 'Pdf' | 'Image' | 'Text' => {
  const mimeType = file.type.toLowerCase();

  if (mimeType === 'application/pdf') {
    return 'Pdf';
  } else if (mimeType.startsWith('image/')) {
    return 'Image';
  } else {
    return 'Text';
  }
};

/**
 * Get mimeType from File object with fallback
 */
export const getFileMimeType = (file: File): string =>
  file.type || 'application/octet-stream';

/**
 * Compute display state for a file with upload and optimistic update states.
 * Consolidates the repeated state calculations used in photo card components.
 */
export const getFileDisplayState = (file: {
  uploadState?: string;
  status?: string;
  downloadUrl?: string;
  previewUrl?: string;
  error?: string;
  _optimistic?: {
    type?: string;
    status?: string;
    error?: string;
  };
}) => {
  const hasUploadError = file.uploadState === 'error';
  const hasOptimisticError = file._optimistic?.status === 'error';
  const hasError = hasUploadError || hasOptimisticError;
  const errorMessage = file.error || file._optimistic?.error;

  const isActivelyUploading =
    file.uploadState === 'uploading' || file.uploadState === 'pending';
  const isPending = file._optimistic?.status === 'pending';
  const isUploading =
    !hasError &&
    (isActivelyUploading || (file.status !== 'Active' && !file.downloadUrl));

  const displayImageUrl = file.downloadUrl || file.previewUrl;

  return {
    hasUploadError,
    hasOptimisticError,
    hasError,
    errorMessage,
    isActivelyUploading,
    isPending,
    isUploading,
    displayImageUrl,
  };
};

/**
 * Validate file before upload
 */
export const validateFile = (
  file: File,
  allowedMimeType?: string,
): { isValid: boolean; error?: string } => {
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: 'File size must be less than 10MB',
    };
  }

  if (allowedMimeType) {
    if (allowedMimeType.endsWith('/*')) {
      const baseType = allowedMimeType.slice(0, -2);
      if (!file.type.startsWith(baseType)) {
        return {
          isValid: false,
          error: `Only ${baseType} files are allowed`,
        };
      }
    } else {
      if (file.type !== allowedMimeType) {
        return {
          isValid: false,
          error: `Only ${allowedMimeType} files are allowed`,
        };
      }
    }
  } else {
    if (!(SUPPORTED_MIME_TYPES as readonly string[]).includes(file.type)) {
      return {
        isValid: false,
        error: 'File type not supported',
      };
    }
  }

  return { isValid: true };
};
